'use client';

import { memo, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Message } from '@/types';
import MockWorkflowMessage from '@/components/chat/MockWorkflowMessage';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useChatContext } from '@/contexts/ChatContext';
import { approveTool } from '@/api/chats';

const MemoizedReactMarkdown = memo(
  ReactMarkdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    (prevProps as any).className === (nextProps as any).className,
);

/** Chip for ClickUp task/list link — same style as integration mention chip */
const ClickUpChip = memo(({ name, id }: { name: string; id: string }) => (
  <span
    className="mention-chip inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md text-xs font-medium theme-text border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] align-middle mx-0.5 shrink-0"
    title={id}
  >
    <img src="/icons/integrations/clickup.svg" alt="" className="w-4 h-4 rounded object-contain" />
    <span className="truncate max-w-[180px]">{name}</span>
  </span>
));
ClickUpChip.displayName = 'ClickUpChip';

const CLICKUP_LINK_REGEX = /\[ClickUp:\s*([^\]]+)\]\((clickup:[^)]+)\)/g;

/** Message shown by API when user has insufficient credits; when this appears we show a Pro upgrade card */
const CREDITS_INSUFFICIENT_MESSAGE = 'Créditos insuficientes. Adquira mais créditos para continuar.';

function isCreditsInsufficientMessage(content: string | undefined): boolean {
  if (!content || !content.trim()) return false;
  const trimmed = content.trim();
  return trimmed === CREDITS_INSUFFICIENT_MESSAGE || trimmed.includes('Créditos insuficientes');
}

function parseContentWithClickUpLinks(
  content: string,
): Array<{ type: 'text'; value: string } | { type: 'clickup'; name: string; id: string }> {
  const segments: Array<{ type: 'text'; value: string } | { type: 'clickup'; name: string; id: string }> = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  CLICKUP_LINK_REGEX.lastIndex = 0;
  while ((m = CLICKUP_LINK_REGEX.exec(content)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, m.index) });
    }
    const name = m[1].trim();
    const id = m[2].replace(/^clickup:/, '');
    segments.push({ type: 'clickup', name, id });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: content }];
}

interface MessageProps {
  message: Message;
  agentName?: string;
}

interface AgentMessageProps {
  message: Message;
  agentName?: string;
  startTypingAnimation?: (messageId: string) => void;
  stopTypingAnimation?: (messageId: string) => void;
}
// Prevent re-animating the same message when the list re-renders / remounts.
const animatedAssistantMessageIds = new Set<string>();

const UserMessage = memo(({ message }: MessageProps) => {
  const { user } = useAuth();

  useEffect(() => {
    return () => {
      for (const att of message.attachments || []) {
        if (att.previewUrl && att.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(att.previewUrl);
        }
      }
    };
  }, [message.attachments]);

  const displayName = user?.name || user?.email || 'Usuário';
  const initials = (displayName || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start gap-2 justify-end"
    >
      <div className="max-w-[85%] px-2.5 py-1.5 rounded-2xl rounded-tr-sm chat-user-bubble">
        {message.attachments && message.attachments.length > 0 && (
          <div className="mb-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {message.attachments.map((att) => {
                const isImage = !!att.previewUrl && (att.type?.startsWith('image/') ?? false);
                if (isImage) {
                  return (
                    <a
                      key={`${att.name}-${att.previewUrl}`}
                      href={att.previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block overflow-hidden rounded-xl border theme-border"
                      title={att.name}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={att.previewUrl}
                        alt={att.name}
                        className="w-full h-28 object-cover"
                      />
                    </a>
                  );
                }

                return (
                  <div
                    key={`${att.name}-${att.size ?? 0}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border theme-border text-xs bg-card text-[color:var(--chat-ink)]/80"
                    title={att.name}
                  >
                    <span className="flex-shrink-0 text-[color:var(--accent-pink-500)]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.44 11.05l-8.49 8.49a5 5 0 01-7.07-7.07l9.19-9.19a3.5 3.5 0 014.95 4.95l-9.19 9.19a2 2 0 01-2.83-2.83l8.49-8.49" />
                      </svg>
                    </span>
                    <span className="truncate">{att.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed chat-user-text inline-flex flex-wrap items-center gap-0">
          {parseContentWithClickUpLinks(message.content || '').map((seg, i) =>
            seg.type === 'text' ? (
              <span key={i}>{seg.value}</span>
            ) : (
              <ClickUpChip key={i} name={seg.name} id={seg.id} />
            ),
          )}
        </p>
      </div>
      <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-[#18181b] shadow-sm shadow-black/10 dark:shadow-black/30 shrink-0">
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt={displayName}
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          <span className="text-[11px] font-semibold tracking-wide text-[color:var(--foreground)]">
            {initials || 'U'}
          </span>
        )}
      </span>
    </motion.div>
  );
});
UserMessage.displayName = 'UserMessage';

const AgentMessage = memo(
  ({ message, agentName, startTypingAnimation, stopTypingAnimation }: AgentMessageProps) => {
    // Only show loading state if there is no content AND no tool usage
    const hasToolUsage = message.metadata?.tool_usage && Array.isArray(message.metadata.tool_usage) && message.metadata.tool_usage.length > 0;
    const hasWorkflow = !!(message.metadata?.workflow || message.metadata?.mock_workflow);
    const isLoading = !message.content && !hasToolUsage && !hasWorkflow;
    const reduceMotion = useReducedMotion();
    const toast = useToast();
    const { deleteMessageById, regenerateMessage, sendMessage, isStreaming } = useChatContext();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleRegenerate = useCallback(async () => {
      setIsRegenerating(true);
      try {
        await regenerateMessage(message.id);
        toast.success('Regenerado', 'Mensagem regenerada com sucesso.');
      } catch {
        toast.error('Não foi possível regenerar a mensagem.');
      } finally {
        setIsRegenerating(false);
      }
    }, [message.id, regenerateMessage, toast]);

    const handleSuggestionClick = useCallback((suggestion: string) => {
      if (!isStreaming) {
        const textToSend = normalizeSuggestionText(suggestion, 999).trim() || suggestion;
        sendMessage(textToSend);
      }
    }, [isStreaming, sendMessage]);

    const handleDelete = useCallback(async () => {
      setIsDeleting(true);
      try {
        await deleteMessageById(message.id);
        toast.success('Apagado', 'Mensagem removida.');
      } catch {
        toast.error('Não foi possível apagar a mensagem.');
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      }
    }, [message.id, deleteMessageById, toast]);

    const shouldAnimateOnMount =
      !reduceMotion &&
      !!message.isNew &&
      !!message.content &&
      !animatedAssistantMessageIds.has(message.id);

    const [typedContent, setTypedContent] = useState<string>(() =>
      shouldAnimateOnMount ? '' : message.content,
    );
    const [isTyping, setIsTyping] = useState<boolean>(() => shouldAnimateOnMount);

    useEffect(() => {
      if (!message.content) return;

      // Never animate historical messages.
      if (!message.isNew) return;

      const fullText = message.content;
      let bootRafId = 0;
      const charsPerSecond = 85;

      // If user prefers reduced motion, show content immediately and mark as "done".
      if (reduceMotion) {
        bootRafId = requestAnimationFrame(() => {
          setTypedContent(message.content);
          setIsTyping(false);
          animatedAssistantMessageIds.add(message.id);
          stopTypingAnimation?.(message.id);
        });
        return () => cancelAnimationFrame(bootRafId);
      }

      // Don't re-animate messages we've already animated.
      if (animatedAssistantMessageIds.has(message.id)) {
        bootRafId = requestAnimationFrame(() => {
          setTypedContent(message.content);
          setIsTyping(false);
          stopTypingAnimation?.(message.id);
        });
        return () => cancelAnimationFrame(bootRafId);
      }

      let startedAt = 0;
      let rafId = 0;
      let lastLen = -1;

      const tick = () => {
        const elapsedMs = performance.now() - startedAt;
        const nextLen = Math.min(
          fullText.length,
          Math.floor((elapsedMs / 1000) * charsPerSecond),
        );

        if (nextLen !== lastLen) {
          lastLen = nextLen;
          setTypedContent(fullText.slice(0, nextLen));
        }

        if (nextLen >= fullText.length) {
          setIsTyping(false);
          animatedAssistantMessageIds.add(message.id);
          stopTypingAnimation?.(message.id);
          return;
        }

        rafId = requestAnimationFrame(tick);
      };

      bootRafId = requestAnimationFrame(() => {
        setTypedContent('');
        setIsTyping(true);
        startTypingAnimation?.(message.id);
        startedAt = performance.now();
        rafId = requestAnimationFrame(tick);
      });

      return () => {
        cancelAnimationFrame(bootRafId);
        cancelAnimationFrame(rafId);
        stopTypingAnimation?.(message.id);
      };
    }, [
      message.id,
      message.content,
      message.isNew,
      reduceMotion,
      startTypingAnimation,
      stopTypingAnimation,
    ]);

    const handleCopy = async () => {
      try {
        const text = message.content || '';
        if (!text) return;
        await navigator.clipboard.writeText(text);
        toast.success('Copiado', 'Mensagem copiada para a área de transferência.');
      } catch {
        toast.error('Não foi possível copiar');
        // ignore
      }
    };

    const isPlanningMode = message.metadata?.tool_usage?.some((t: any) =>
      (typeof t === 'string' && t.includes('plan_task')) ||
      (t.tool_name === 'plan_task')
    );

    const isDocumentMode = message.metadata?.tool_usage?.some((t: any) =>
      (typeof t === 'string' && t.includes('create_html_document')) ||
      (t.tool_name === 'create_html_document')
    );

    const mockWorkflow = message.metadata?.mock_workflow as any;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start gap-2"
      >
        {/* Avatar - always show the logo */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
          <svg
            fill="none"
            height="48"
            viewBox="0 0 40 48"
            width="40"
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8"
          >
            <path
              clipRule="evenodd"
              d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z"
              fill="var(--nt-primary)"
              fillRule="evenodd"
            />
          </svg>
        </div>

        <div className="max-w-[80%] flex flex-col gap-1">
          {/* Agent name and badges */}
          {agentName && (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs theme-text font-semibold flex items-center gap-2">
                {agentName}
                {isPlanningMode && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    Modo Planejamento
                  </span>
                )}
                {isDocumentMode && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    Modo Documento
                  </span>
                )}
              </span>
            </div>
          )}

          {mockWorkflow ? (
            <MockWorkflowMessage messageId={message.id} workflow={mockWorkflow} />
          ) : (
            <>
              {/* 1. Chain of Thought / Reasoning — appears FIRST, outside any bubble,
                     since reasoning happens before actions and responses */}
              {message.metadata?.reasoning_steps && message.metadata.reasoning_steps.length > 0 && (
                <ChainOfThought defaultOpen={false}>
                  <ChainOfThoughtHeader>Processo de Raciocínio</ChainOfThoughtHeader>
                  <ChainOfThoughtContent>
                    {message.metadata.reasoning_steps.map((step, idx) => {
                      const isLast = idx === (message.metadata?.reasoning_steps?.length ?? 0) - 1;
                      const rawStatus = (step as any)?.status;
                      const status =
                        rawStatus === 'complete' || rawStatus === 'active' || rawStatus === 'pending'
                          ? rawStatus
                          : 'complete';
                      return (
                        <ChainOfThoughtStep
                          key={idx}
                          label={step.title || step.action || `Etapa ${idx + 1}`}
                          description={step.content || step.result || undefined}
                          status={status}
                          isLast={isLast}
                        />
                      );
                    })}
                  </ChainOfThoughtContent>
                </ChainOfThought>
              )}

              {/* Reasoning Content (raw text, shown when no structured steps) */}
              {message.metadata?.reasoning_content && !message.metadata?.reasoning_steps?.length && (
                <Reasoning isStreaming={false}>
                  <ReasoningTrigger />
                  <ReasoningContent>
                    {message.metadata.reasoning_content}
                  </ReasoningContent>
                </Reasoning>
              )}

              {/* 2. Show ThinkingLoader only when there's nothing else to show */}
              {isLoading && <ThinkingLoader />}

              {/* 3. Tasks — after reasoning, before plan */}
              {(() => {
                const tools = message.metadata?.tool_usage;
                if (isLoading || !Array.isArray(tools) || tools.length === 0) return null;

                const mapToolStateToTaskStatus = (state: any): TaskStatus => {
                  const s = String(state || '').toLowerCase();
                  if (s === 'output-error' || s === 'error') return 'error';
                  if (s === 'result' || s === 'output-available' || s === 'approved') return 'completed';
                  if (s === 'pending_approval' || s === 'approval-requested') return 'pending';
                  return 'in_progress';
                };

                const items = tools.map((t: any) => {
                  const rawName = t?.toolName || 'Ferramenta';
                  const displayName = resolveToolDisplayName(rawName, t?.args);
                  return {
                    title: displayName,
                    status: mapToolStateToTaskStatus(t?.state),
                  };
                });

                const totalCount = items.length;
                const completedCount = items.filter((i) => i.status === 'completed').length;
                const hasError = items.some((i) => i.status === 'error');
                const hasInProgressOrPending = items.some(
                  (i) => i.status === 'in_progress' || i.status === 'pending',
                );
                const overallStatus = hasError
                  ? 'error'
                  : hasInProgressOrPending
                    ? 'in_progress'
                    : 'completed';

                return (
                  <Task defaultOpen={false} status={overallStatus}>
                    <TaskTrigger
                      title="Tarefas"
                      status={overallStatus}
                      completedCount={completedCount}
                      totalCount={totalCount}
                    />
                    <TaskContent>
                      {items.map((it, idx) => (
                        <TaskItem key={`${idx}-${it.title}`} status={it.status}>
                          {it.title}
                        </TaskItem>
                      ))}
                    </TaskContent>
                  </Task>
                );
              })()}

              {/* 4. Plan Mode — when content arrives BEFORE a pending tool approval,
                     wrap the content in a Plan card so it reads as "here's what I'll do" */}
              {(() => {
                const tools = message.metadata?.tool_usage;
                const hasPendingOrRunning = Array.isArray(tools) && tools.some(
                  (t: any) => t.state === 'pending_approval' || t.state === 'call'
                );
                const isPlanMode = !isLoading && message.content && hasPendingOrRunning;

                if (isPlanMode) {
                  // Parse plan steps from content lines (bullet points / numbered items)
                  const lines = message.content.split('\n').filter((l: string) => l.trim());
                  const planTitle = lines[0]?.replace(/^\[PLAN\]\s*/i, '').trim() || 'Plano de Execução';
                  const stepLines = lines.slice(1).filter((l: string) => /^[\s]*[-•*\d]/.test(l));
                  const descLines = lines.slice(1).filter((l: string) => !/^[\s]*[-•*\d]/.test(l));

                  return (
                    <Plan isStreaming={hasPendingOrRunning} defaultOpen={true}>
                      <PlanHeader>
                        <PlanTitle>{planTitle}</PlanTitle>
                        {descLines.length > 0 && (
                          <PlanDescription>{descLines.join(' ')}</PlanDescription>
                        )}
                      </PlanHeader>
                      {stepLines.length > 0 && (
                        <>
                          <PlanTrigger />
                          <PlanContent>
                            {stepLines.map((line, idx) => (
                              <PlanStep
                                key={idx}
                                label={line.replace(/^[\s]*[-•*\d.)\]]+\s*/, '').trim()}
                                status={hasPendingOrRunning ? 'pending' : 'complete'}
                                isLast={idx === stepLines.length - 1}
                              />
                            ))}
                          </PlanContent>
                        </>
                      )}
                    </Plan>
                  );
                }
                return null;
              })()}
            </>
          )}

          {/* 4. Tool Invocations — shown after plan/reasoning, before final content */}
          {!mockWorkflow && !isLoading && message.metadata?.tool_usage && Array.isArray(message.metadata.tool_usage) && (
            <div className="flex flex-col gap-2">
              {message.metadata.tool_usage.map((tool: any, idx: number) => {
                const rawName = tool.toolName || (typeof tool === 'string' ? tool : 'Ferramenta Desconhecida');
                const toolInput = tool.args;
                const toolOutput = tool.result;
                // For historical messages (not new/streaming), missing state means
                // the tool already finished — default to 'result' instead of 'call'.
                const toolState = tool.state || (toolOutput ? 'result' : (message.isNew ? 'call' : 'result'));
                const displayName = resolveToolDisplayName(rawName, toolInput);

                // Show Confirmation component for pending approval
                if (toolState === 'pending_approval') {
                  return (
                    <ToolApprovalCard
                      key={idx}
                      toolName={rawName}
                      toolInput={toolInput}
                      toolCallId={tool.toolCallId}
                    />
                  );
                }

                // Show resolved approval state (approved / rejected) with who approved
                if (toolState === 'approved' || toolState === 'rejected') {
                  const approver = tool.approvedBy;
                  const approvedAt = tool.approvedAt
                    ? new Date(tool.approvedAt).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : null;

                  return (
                    <Confirmation
                      key={idx}
                      state="approval-responded"
                      approval={{ id: tool.toolCallId, approved: toolState === 'approved' }}
                    >
                      <ConfirmationAccepted>
                        <div className="flex flex-col gap-0.5">
                          <span><strong>{displayName}</strong> foi aprovado</span>
                          {(approver || approvedAt) && (
                            <span className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                              {approver && <>por <strong>{approver}</strong></>}
                              {approver && approvedAt && ' · '}
                              {approvedAt && <>{approvedAt}</>}
                            </span>
                          )}
                        </div>
                      </ConfirmationAccepted>
                      <ConfirmationRejected>
                        <div className="flex flex-col gap-0.5">
                          <span><strong>{displayName}</strong> foi rejeitado</span>
                          {(approver || approvedAt) && (
                            <span className="text-xs text-red-600/70 dark:text-red-400/70">
                              {approver && <>por <strong>{approver}</strong></>}
                              {approver && approvedAt && ' · '}
                              {approvedAt && <>{approvedAt}</>}
                            </span>
                          )}
                        </div>
                      </ConfirmationRejected>
                    </Confirmation>
                  );
                }

                // Show Tool component for executed tools (with all state support)
                const toolApprover = tool.approvedBy;
                const toolApprovedAt = tool.approvedAt
                  ? new Date(tool.approvedAt).toLocaleString('pt-BR', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })
                  : null;

                const integration = getToolIntegration(rawName, toolInput);
                return (
                  <Tool key={idx} state={toolState}>
                    <ToolHeader toolName={displayName} state={toolState} integration={integration} />
                    <ToolContent>
                      {toolApprover && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--foreground-muted)]">
                          <svg className="h-3 w-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>
                            Aprovado por <strong className="text-[color:var(--foreground)]">{toolApprover}</strong>
                            {toolApprovedAt && <> · {toolApprovedAt}</>}
                          </span>
                        </div>
                      )}
                      {toolInput && Object.keys(toolInput).length > 0 && (
                        <ToolInput input={toolInput} />
                      )}
                      {(toolOutput || (toolState === 'output-error' && tool.result)) && (
                        <ToolOutput
                          output={toolState === 'output-error' ? undefined : toolOutput}
                          errorText={toolState === 'output-error' && tool.result != null
                            ? (typeof tool.result === 'string' ? tool.result : String(tool.result))
                            : undefined}
                        />
                      )}
                    </ToolContent>
                  </Tool>
                );
              })}
            </div>
          )}

          {/* 5. Message content — shown LAST as the final response bubble
                 (hidden during plan mode since content is inside the Plan card) */}
          {!isLoading && message.content && (() => {
            const tools = message.metadata?.tool_usage;
            const hasPendingOrRunning = Array.isArray(tools) && tools.some(
              (t: any) => t.state === 'pending_approval' || t.state === 'call'
            );
            // Don't show bubble if content is already displayed in Plan card
            if (hasPendingOrRunning) return null;
            // Strip [PLAN] prefix and [SUGGESTIONS]...[/SUGGESTIONS] block from displayed content
            const stripTags = (text: string) =>
              text
                .replace(/^\[PLAN\]\s*/i, '')
                .replace(/\[SUGGESTIONS\][\s\S]*?\[\/SUGGESTIONS\]/gi, '')
                .trim();
            const cleanContent = stripTags(message.content);
            const cleanTyped = stripTags(typedContent);
            if (!cleanContent) return null;
            const showProUpgradeCard = isCreditsInsufficientMessage(cleanContent);
            const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
              p: ({ children, ...rest }) => (
                <p {...rest} style={{ whiteSpace: 'pre-line' }}>{children}</p>
              ),
              a: (props) => {
                const href = props.href ?? '';
                if (href.startsWith('clickup:')) {
                  const id = href.replace(/^clickup:/, '');
                  const rawName = typeof props.children === 'string' ? props.children : (Array.isArray(props.children) ? props.children.join('') : String(props.children ?? ''));
                  const name = rawName.replace(/^ClickUp:\s*/i, '').trim() || rawName;
                  return <ClickUpChip name={name} id={id} />;
                }
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 decoration-1 text-[color:var(--nt-primary)] hover:opacity-80 transition-opacity break-all"
                  >
                    {props.children}
                  </a>
                );
              },
            };
            return (
              <div className="flex flex-col gap-3">
                <div className="px-2.5 py-1.5 rounded-2xl rounded-tl-sm chat-agent-bubble">
                  {isTyping ? (
                    <div
                      className={`chat-markdown prose prose-sm max-w-none text-[14px] leading-[1.9] theme-text
                  prose-p:text-[color:var(--chat-ink)]/90 prose-p:my-4
                  prose-headings:text-[color:var(--foreground)] prose-headings:mt-6 prose-headings:mb-3
                  prose-strong:text-[color:var(--foreground)]
                  prose-ul:my-4 prose-ol:my-4 prose-li:my-2
                  prose-hr:my-5 prose-hr:border-0 prose-hr:border-t prose-hr:border-[color:var(--card-border)] prose-hr:opacity-70
                  prose-blockquote:my-4
                  prose-code:text-[color:var(--accent-pink-500)] prose-code:bg-[color:var(--chat-code-bg)] prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  prose-pre:my-4 prose-pre:p-4 prose-pre:border prose-pre:bg-[color:var(--chat-pre-bg)] prose-pre:theme-border`}
                    >
                      <MemoizedReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {`${normalizeMarkdownContent(cleanTyped)}▍`}
                      </MemoizedReactMarkdown>
                    </div>
                  ) : (
                    <div
                      className={`chat-markdown prose prose-sm max-w-none text-[14px] leading-[1.9] theme-text
                  prose-p:text-[color:var(--chat-ink)]/90 prose-p:my-4
                  prose-headings:text-[color:var(--foreground)] prose-headings:mt-6 prose-headings:mb-3
                  prose-strong:text-[color:var(--foreground)]
                  prose-ul:my-4 prose-ol:my-4 prose-li:my-2
                  prose-hr:my-5 prose-hr:border-0 prose-hr:border-t prose-hr:border-[color:var(--card-border)] prose-hr:opacity-70
                  prose-blockquote:my-4
                  prose-code:text-[color:var(--accent-pink-500)] prose-code:bg-[color:var(--chat-code-bg)] prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  prose-pre:my-4 prose-pre:p-4 prose-pre:border prose-pre:bg-[color:var(--chat-pre-bg)] prose-pre:theme-border`}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
                        {normalizeMarkdownContent(cleanContent)}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
                {showProUpgradeCard && (
                  <CreditsProUpgradeCard />
                )}
              </div>
            );
          })()}

          {/* Action bar (right aligned) */}
          {!isLoading && (
            <div className="flex justify-end">
              <div className="relative mt-2 inline-flex items-center gap-1 rounded-xl border theme-border bg-white dark:bg-[#18181b] px-1 py-1">
                {/* Regeneration badge */}
                {message.metadata?.regenerationCount && message.metadata.regenerationCount > 0 && (
                  <span
                    className="h-8 inline-flex items-center gap-1 px-2 text-[11px] text-[color:var(--foreground-muted)] font-medium"
                    title={`Regenerado ${message.metadata.regenerationCount}x`}
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {message.metadata.regenerationCount}x
                  </span>
                )}

                {/* Copy */}
                <button
                  type="button"
                  onClick={handleCopy}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 theme-text disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Copiar"
                  title="Copiar"
                  disabled={!message.content}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>

                {/* Regenerate */}
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 theme-text disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Gerar novamente"
                  title="Gerar novamente"
                  disabled={isStreaming || isRegenerating || isDeleting}
                >
                  {isRegenerating ? (
                    <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-3-6.7" />
                      <path d="M21 3v7h-7" />
                    </svg>
                  )}
                </button>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-[color:var(--foreground-muted)] hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Apagar"
                  title="Apagar mensagem"
                  disabled={isStreaming || isRegenerating || isDeleting}
                >
                  {isDeleting ? (
                    <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                    </svg>
                  )}
                </button>

                {/* Delete confirmation popover */}
                <AnimatePresence>
                  {showDeleteConfirm && (
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full right-0 mb-2 z-50 min-w-[200px] rounded-xl border theme-border bg-white dark:bg-[#18181b] shadow-lg p-3"
                    >
                      <p className="text-sm text-[color:var(--foreground)] font-medium mb-1">
                        Apagar mensagem?
                      </p>
                      <p className="text-xs text-[color:var(--foreground-muted)] mb-3">
                        Esta ação não pode ser desfeita.
                      </p>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(false)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border theme-border text-[color:var(--foreground-muted)] hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {isDeleting ? 'Apagando...' : 'Apagar'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* 7. Suggestions — shown after action bar when available and not streaming */}
          {!isLoading && !isStreaming && message.metadata?.suggestions && Array.isArray(message.metadata.suggestions) && message.metadata.suggestions.length > 0 && (
            <Suggestions>
              {message.metadata.suggestions.map((suggestion: string, idx: number) => (
                <Suggestion
                  key={idx}
                  suggestion={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={isStreaming}
                />
              ))}
            </Suggestions>
          )}
        </div>
      </motion.div >
    );
  });
AgentMessage.displayName = 'AgentMessage';

import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@/components/ai-elements/tool';
import {
  ChainOfThought,
  ChainOfThoughtHeader,
  ChainOfThoughtContent,
  ChainOfThoughtStep,
} from '@/components/ai-elements/chain-of-thought';
import {
  Plan,
  PlanHeader,
  PlanTitle,
  PlanDescription,
  PlanTrigger,
  PlanContent,
  PlanStep,
} from '@/components/ai-elements/plan';
import {
  Confirmation,
  ConfirmationRequest,
  ConfirmationAccepted,
  ConfirmationRejected,
  ConfirmationActions,
  ConfirmationAction,
} from '@/components/ai-elements/confirmation';
import {
  Suggestions,
  Suggestion,
  normalizeSuggestionText,
} from '@/components/ai-elements/suggestion';
import {
  Task,
  TaskContent,
  TaskItem,
  TaskTrigger,
  type TaskStatus,
} from '@/components/ai-elements/task';


// ─── Tool Display Name Resolution ────────────────────────────────────

/**
 * Ensures line breaks render in markdown:
 * 1. Fix double-encoded \\n → real \n
 * 2. Turn every single \n into markdown hard break (two spaces + \n) so the
 *    parser emits <br>; leave \n\n as-is for paragraph breaks.
 */
function normalizeMarkdownContent(text: string): string {
  let out = text.replace(/\\n/g, '\n');
  out = out.replace(/(?<!\n)\n(?!\n)/g, '  \n');
  return out;
}

/** Known team member IDs → friendly display names */
const MEMBER_NAMES: Record<string, string> = {
  clickupassistant: 'ClickUp Assistant',
  linkedin: 'LinkedIn',
  docsassistant: 'Docs Assistant',
};

/** Integration icon to show in tool header: delegate_task_to_member → clickup | linkedin */
function getToolIntegration(rawName: string, args?: Record<string, any>): 'clickup' | 'linkedin' | undefined {
  if (rawName !== 'delegate_task_to_member' || !args?.member_id) return undefined;
  const id = String(args.member_id).toLowerCase();
  if (id === 'clickupassistant') return 'clickup';
  if (id === 'linkedin') return 'linkedin';
  return undefined;
}

/**
 * Resolves a raw tool name + args into a user-friendly display name.
 *
 * - `delegate_task_to_member` with `member_id: "clickupassistant"` → "ClickUp Assistant"
 * - `clickup_list_tasks` → "List Tasks (ClickUp)"
 * - `create_html_document` → "Create HTML Document"
 * - Generic snake_case → Title Case
 */
function resolveToolDisplayName(rawName: string, args?: Record<string, any>): string {
  // Special: delegation → use member name
  if (rawName === 'delegate_task_to_member' && args?.member_id) {
    const memberId = String(args.member_id).toLowerCase();
    return MEMBER_NAMES[memberId] || formatSnakeCase(args.member_id);
  }

  return formatSnakeCase(rawName);
}

/** `snake_case` / `kebab-case` → `Title Case` */
function formatSnakeCase(name: string): string {
  return name
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Returns a short description for the approval prompt depending on the tool.
 */
function resolveApprovalMessage(rawName: string, args?: Record<string, any>): React.ReactNode {
  if (rawName === 'delegate_task_to_member' && args?.member_id) {
    const memberName = MEMBER_NAMES[String(args.member_id).toLowerCase()] || formatSnakeCase(args.member_id);
    return (
      <>
        Delegar tarefa para <strong>{memberName}</strong>?
      </>
    );
  }

  return (
    <>
      Executar <strong>{formatSnakeCase(rawName)}</strong>?
    </>
  );
}

// ─── Tool Approval Card (encapsulates loading/error state) ───────────
const ToolApprovalCard = ({
  toolName,
  toolInput,
  toolCallId,
}: {
  toolName: string;
  toolInput: any;
  toolCallId: string;
}) => {
  const [loading, setLoading] = useState<'approve' | 'reject' | null>(null);
  const toast = useToast();
  const { user } = useAuth();
  const { updateToolState } = useChatContext();

  const handleAction = async (approved: boolean) => {
    setLoading(approved ? 'approve' : 'reject');
    try {
      const approverName = user?.name || user?.email || 'Usuário';
      await approveTool(toolCallId, approved, approverName);
      // Store who approved/rejected and when
      const approvalMeta = {
        approvedBy: approverName,
        approvedAt: new Date().toISOString(),
      };
      // Immediately update local state so the UI transitions
      updateToolState(toolCallId, approved ? 'call' : 'rejected', undefined, approvalMeta);
    } catch (error) {
      console.error('Failed to handle tool approval', error);
      toast.error(approved ? 'Falha ao aprovar' : 'Falha ao rejeitar');
      setLoading(null);
    }
  };

  const approvalMsg = resolveApprovalMessage(toolName, toolInput);

  // For delegation tools, show only the task (not the member_id which is already in the title)
  const visibleInput = toolName === 'delegate_task_to_member' && toolInput?.task
    ? { task: toolInput.task }
    : toolInput;

  return (
    <Confirmation state="approval-requested" approval={{ id: toolCallId }}>
      <ConfirmationRequest>
        {approvalMsg}
        {visibleInput && (
          <pre className="mt-2 p-2 rounded-lg bg-black/5 dark:bg-white/5 text-xs overflow-auto max-h-40 text-[color:var(--foreground-muted)]">
            {JSON.stringify(visibleInput, null, 2)}
          </pre>
        )}
      </ConfirmationRequest>
      <ConfirmationActions>
        <ConfirmationAction
          variant="reject"
          loading={loading === 'reject'}
          disabled={loading !== null}
          onClick={() => handleAction(false)}
        >
          Rejeitar
        </ConfirmationAction>
        <ConfirmationAction
          variant="approve"
          loading={loading === 'approve'}
          disabled={loading !== null}
          onClick={() => handleAction(true)}
        >
          Aprovar
        </ConfirmationAction>
      </ConfirmationActions>
    </Confirmation>
  );
};

const ThinkingLoader = () => {
  return (
    <Reasoning isStreaming={true} className="w-full max-w-md">
      <ReasoningTrigger />
      <ReasoningContent>
        Analisando contexto e gerando resposta...
      </ReasoningContent>
    </Reasoning>
  );
};

const OPEN_SETTINGS_BILLING_EVENT = 'paladino-open-settings-billing';

/** Card shown below the "Créditos insuficientes" message; opens Settings on Billing tab. */
const CreditsProUpgradeCard = memo(() => {
  const handleUpgrade = () => {
    window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_BILLING_EVENT));
  };

  return (
    <div className="rounded-2xl border theme-border overflow-hidden bg-[color:color-mix(in_oklab,var(--nt-primary)_08%,var(--card-bg))] dark:bg-[color:color-mix(in_oklab,var(--nt-primary)_12%,var(--card-bg))] max-w-md">
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold theme-text flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[color:var(--nt-primary)]/15 text-[color:var(--nt-primary)]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            Paladino Pro
          </div>
          <p className="text-xs theme-text-muted mt-1.5">
            Mais créditos diários, membros e integrações. Assine e continue usando sem limites.
          </p>
        </div>
        <button
          type="button"
          onClick={handleUpgrade}
          className="shrink-0 inline-flex items-center justify-center h-10 px-4 rounded-xl text-sm font-semibold bg-[color:var(--nt-primary)] text-white hover:brightness-95 transition-all shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)]"
        >
          Assinar Pro
        </button>
      </div>
    </div>
  );
});
CreditsProUpgradeCard.displayName = 'CreditsProUpgradeCard';

export { UserMessage, AgentMessage };
export { OPEN_SETTINGS_BILLING_EVENT };
